import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/browser';
import moment from 'moment';

import useComponentMounted from '../../../../hooks/useComponentMounted';
import UserConfig from '../../../../Model/UserConfig';
import CycleHealth from '../../../Model/CycleHealth';
import UserNutritionProfile from '../../../Model/UserNutritionProfile';
import { BiologicalSex } from '../../../utils/macroGoals';
import { DATE_FORMAT, getCycleWeekSummary } from '../../../utils/cycle';

// A feed can show several check-in cards for one client, so each client's reads are shared for a few minutes.
const CACHE_TTL_MS = 5 * 60 * 1000;
const cache = new Map();

/**
 * Runs `load` once per `key` within CACHE_TTL_MS and hands every caller the same promise. A failed read is dropped
 * from the cache, so the next card tries again.
 */
const cached = (key, load) => {
  const entry = cache.get(key);
  if (entry && Date.now() - entry.loadedAt < CACHE_TTL_MS) {
    return entry.promise;
  }
  const promise = load();
  cache.set(key, { loadedAt: Date.now(), promise });
  promise.catch(() => cache.delete(key));
  return promise;
};

// Empties the shared reads, e.g. between tests.
const clearCycleDataCache = () => cache.clear();

// The client's sex, cycle settings and UTC offset (minutes, from their app's last visit).
const loadClientCycleProfile = (userId) => cached(`profile:${userId}`, async () => {
  const cycleHealthDoc = new CycleHealth(userId);
  const [nutritionProfile, userConfig] = await Promise.all([
    UserNutritionProfile.getById(userId),
    UserConfig.getConfigById(userId),
    cycleHealthDoc.init(),
  ]);
  return {
    isFemale: nutritionProfile?.biologicalSex === BiologicalSex.FEMALE,
    hasCycleHealth: cycleHealthDoc.exists,
    settings: cycleHealthDoc.settings,
    utcOffset: userConfig?.lastUTCOffset,
  };
});

const loadDayLogs = (userId, windowEndId) => cached(
  `dayLogs:${userId}:${windowEndId}`,
  () => CycleHealth.getDayLogs(userId, windowEndId),
);

/**
 * The client's local date (YYYY-MM-DD) for a moment in time. Day logs are keyed by the client's own dates, so the
 * coach's timezone must not shift them; without a known offset the coach's own is the best guess.
 */
const toClientDateId = (time, utcOffset) => {
  const at = moment(time);
  return (utcOffset === undefined || utcOffset === null ? at : at.utcOffset(utcOffset)).format(DATE_FORMAT);
};

/**
 * The client's cycle data for the coach's check-in view, or null when there's nothing to show: cycle tracking is off
 * (or was never turned on), the client isn't female (biologicalSex on their nutrition profile), the coach may not read
 * it, or the check-in week has neither a phase nor a symptom. The view builds any week from the settings and logs.
 *
 * @param {Object} params
 * @param {string} params.userId The client.
 * @param {boolean} params.isCycleTrackingActive The user doc's flag; nothing is read while tracking is off.
 * @param {Date} params.windowEnd When the check-in was submitted.
 * @returns {{ settings: Object, dayLogs: Object, checkInDateId: string, todayId: string }|null} The settings and day
 *   logs for getCycleWeekSummary, with the check-in day and today as the client's local dates.
 */
const useCycleData = ({ userId, isCycleTrackingActive, windowEnd }) => {
  const [cycleData, setCycleData] = useState(null);
  const isComponentMountedRef = useComponentMounted();
  // Each load supersedes the one before it, so a slow read for a previous client or check-in can't land afterwards.
  const requestRef = useRef(0);
  const windowEndTime = windowEnd?.getTime();

  useEffect(() => {
    requestRef.current += 1;
    const requestId = requestRef.current;
    const isCurrent = () => isComponentMountedRef.current && requestId === requestRef.current;

    setCycleData(null);
    if (!userId || !isCycleTrackingActive || !windowEndTime) return;

    const loadCycleData = async () => {
      try {
        const {
          isFemale, hasCycleHealth, settings, utcOffset,
        } = await loadClientCycleProfile(userId);
        if (!isFemale || !hasCycleHealth) return;

        const checkInDateId = toClientDateId(windowEndTime, utcOffset);
        const dayLogs = await loadDayLogs(userId, checkInDateId);
        const checkInWeek = getCycleWeekSummary(settings, dayLogs, checkInDateId);
        const hasContent = !!checkInWeek.currentPhase || checkInWeek.hasSymptoms;
        if (isCurrent()) {
          setCycleData(hasContent ? {
            settings, dayLogs, checkInDateId, todayId: toClientDateId(Date.now(), utcOffset),
          } : null);
        }
      } catch (error) {
        // The rules only let admins and the client's assigned coach read, and only while tracking is on, so a denied
        // read just means no cycle section for this viewer.
        if (error?.code !== 'permission-denied') {
          Sentry.captureException(error, { extra: { message: 'Error loading cycle summary', userId } });
        }
      }
    };
    loadCycleData();
  }, [userId, isCycleTrackingActive, windowEndTime, isComponentMountedRef]);

  return cycleData;
};

export { clearCycleDataCache };
export default useCycleData;
